import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Token stored in memory + sessionStorage as iOS Safari fallback.
// iOS Safari ITP blocks SameSite=none cookies in some contexts,
// so we persist the token in sessionStorage to survive page refresh.
let _memoryToken = sessionStorage.getItem("_auth_token") || null;

export function setAuthToken(token) {
  _memoryToken = token;
  if (token) {
    sessionStorage.setItem("_auth_token", token);
  } else {
    sessionStorage.removeItem("_auth_token");
  }
}

export function getAuthToken() {
  if (!_memoryToken) {
    _memoryToken = sessionStorage.getItem("_auth_token") || null;
  }
  return _memoryToken;
}

export function clearAuthToken() {
  _memoryToken = null;
  sessionStorage.removeItem("_auth_token");
}

// Send Bearer header from token (cookie + Bearer for maximum compatibility)
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry && !original.url?.includes("/auth/")) {
      original._retry = true;
      try {
        const { data } = await axios.post(`${API}/auth/refresh`, {}, {
          withCredentials: true,
          headers: _memoryToken ? { Authorization: `Bearer ${_memoryToken}` } : {},
        });
        if (data.token) {
          setAuthToken(data.token);
        }
        return api(original);
      } catch {
        clearAuthToken();
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

/** Resolve an image URL (relative or absolute) to the correct full URL via BACKEND_URL. */
export function resolveImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  const match = url.match(/(\/api\/(?:public\/)?files\/.+)/);
  if (match) return `${BACKEND_URL}${match[1]}`;
  // Raw storage path (e.g. "trigotour/profiles/USER_ID/photo.jpg") → route via public files endpoint
  return `${BACKEND_URL}/api/public/files/${url.replace(/^\/+/, "")}`;
}

/** Build authenticated image URL for private files (driver photos, documents). */
export function getAuthImageUrl(docPath) {
  if (!docPath) return null;
  const token = getAuthToken();
  return `${BACKEND_URL}/api/files/${docPath}${token ? `?token=${token}` : ""}`;
}

export default api;
